import { Button } from "@loot-os/ui/components/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@loot-os/ui/components/tabs";
import { formatUnreadBadge } from "@loot-os/ui/lib/notifications";
import { ChevronLeftIcon, SettingsIcon, XIcon } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";

import { type NotificationFilter, useMarkNotificationsRead } from "../../lib/notifications.js";
import { NotificationList } from "./NotificationList.js";
import { NotificationSettings } from "./NotificationSettings.js";

/**
 * What the bell opens: the notifications under All / Unread tabs, or the shopper's settings
 * behind the gear. `showClose` adds a close button, for the phone's full-screen sheet.
 */
export function NotificationPanel({
  unreadCount,
  onClose,
  showClose = false,
}: {
  unreadCount: number;
  onClose: () => void;
  showClose?: boolean;
}) {
  const [view, setView] = useState<"list" | "settings">("list");
  const [tab, setTab] = useState<NotificationFilter>("all");
  const markRead = useMarkNotificationsRead();
  const unreadBadge = formatUnreadBadge(unreadCount);
  // Switching views removes the button that was pressed, so focus moves to its counterpart.
  const backRef = useRef<HTMLButtonElement>(null);
  const gearRef = useRef<HTMLButtonElement>(null);
  const switched = useRef(false);
  useEffect(() => {
    if (!switched.current) return;
    (view === "settings" ? backRef : gearRef).current?.focus();
  }, [view]);
  const show = (next: "list" | "settings") => {
    switched.current = true;
    setView(next);
  };

  const close = showClose ? (
    <Button variant="ghost" size="icon-sm" aria-label="Close notifications" onClick={onClose}>
      <XIcon />
    </Button>
  ) : null;

  if (view === "settings") {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <PanelHeader
          leading={
            <Button
              ref={backRef}
              variant="ghost"
              size="icon-sm"
              aria-label="Back to notifications"
              onClick={() => show("list")}
            >
              <ChevronLeftIcon />
            </Button>
          }
          title="Notification settings"
          trailing={close}
        />
        <NotificationSettings />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PanelHeader
        title="Notifications"
        trailing={
          <>
            <Button
              ref={gearRef}
              variant="ghost"
              size="icon-sm"
              aria-label="Notification settings"
              onClick={() => show("settings")}
            >
              <SettingsIcon />
            </Button>
            {close}
          </>
        }
      />
      <Tabs
        value={tab}
        onValueChange={(value) => setTab(value === "unread" ? "unread" : "all")}
        className="min-h-0 flex-1 gap-0"
      >
        <div className="flex items-center justify-between gap-2 border-b border-border px-2 pb-1">
          <TabsList variant="line">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="unread">
              {unreadBadge ? `Unread · ${unreadBadge}` : "Unread"}
            </TabsTrigger>
          </TabsList>
          <Button
            variant="link"
            size="xs"
            disabled={unreadCount === 0}
            onClick={() => markRead.mutate({ all: true })}
          >
            Mark all as read
          </Button>
        </div>
        {(["all", "unread"] as const).map((filter) => (
          <TabsContent key={filter} value={filter} className="flex min-h-0 flex-1 flex-col">
            <NotificationList
              filter={filter}
              onRead={(id) => markRead.mutate({ ids: [id] })}
              onNavigate={onClose}
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

/** The panel's title row, with an optional leading button and actions on the right. */
function PanelHeader({
  title,
  leading,
  trailing,
}: {
  title: string;
  leading?: ReactNode;
  trailing?: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 px-4 pt-3 pb-2">
      {leading}
      <h2 className="min-w-0 flex-1 truncate text-base font-semibold">{title}</h2>
      <div className="flex shrink-0 items-center gap-1">{trailing}</div>
    </div>
  );
}
